import type { IBase, IList, IRadio, ISelect, ITemplate } from './types';

type PathToken =
  | { type: 'name'; value: string }
  | { type: 'index'; value: number };

const LAYOUT_TYPES = new Set(['group', 'tabs', 'tab', 'anchors', 'explorer']);

/** Active model template used for path→label lookups in the AI assistant UI. */
let currentModelTemplate: ITemplate | null = null;

export const setCurrentModelTemplate = (template: ITemplate | null | undefined): void => {
  currentModelTemplate = template ?? null;
};

export const getCurrentModelTemplate = (): ITemplate | null => currentModelTemplate;

/**
 * Parse a lodash-style model path into name/index tokens.
 * Example: "vehicle_info[0].vehicle_type" → name, index, name
 */
export const parseInputPath = (path: string): PathToken[] => {
  const tokens: PathToken[] = [];
  const re = /([^.[\]]+)|\[(\d+)\]/g;
  let match = re.exec(path);
  while (match) {
    if (match[1] !== undefined) {
      tokens.push({ type: 'name', value: match[1] });
    } else if (match[2] !== undefined) {
      tokens.push({ type: 'index', value: Number(match[2]) });
    }
    match = re.exec(path);
  }
  return tokens;
};

const getChildItems = (node: IBase): IBase[] | undefined => {
  if ('items' in node && Array.isArray((node as { items?: IBase[] }).items)) {
    return (node as { items: IBase[] }).items;
  }
  return undefined;
};

const findByName = (items: IBase[], name: string): IBase | undefined => {
  for (const item of items) {
    if (!LAYOUT_TYPES.has(item.type) && item.name === name) {
      return item;
    }
    const children = getChildItems(item);
    if (children) {
      const found = findByName(children, name);
      if (found) {
        return found;
      }
    }
  }
  return undefined;
};

/**
 * Walk the template input tree to the node for a model-input path.
 */
export const findTemplateNodeByPath = (
  template: ITemplate,
  path: string,
): IBase | undefined => {
  const tokens = parseInputPath(path.trim());
  if (!tokens.length) {
    return undefined;
  }

  const walk = (items: IBase[], tokenIndex: number): IBase | undefined => {
    if (tokenIndex >= tokens.length) {
      return undefined;
    }

    const token = tokens[tokenIndex];
    if (token.type === 'index') {
      return undefined;
    }

    for (const item of items) {
      if (LAYOUT_TYPES.has(item.type)) {
        const children = getChildItems(item);
        if (children) {
          const found = walk(children, tokenIndex);
          if (found) {
            return found;
          }
        }
        continue;
      }

      if (item.name !== token.value) {
        continue;
      }

      if (tokenIndex === tokens.length - 1) {
        return item;
      }

      if (item.type === 'list') {
        const next = tokens[tokenIndex + 1];
        const listItems = (item as IList).items ?? [];
        if (next?.type === 'index') {
          return walk(listItems, tokenIndex + 2);
        }
        return walk(listItems, tokenIndex + 1);
      }

      const children = getChildItems(item);
      if (children) {
        return walk(children, tokenIndex + 1);
      }
    }

    return undefined;
  };

  const matched = walk(template.inputs ?? [], 0);
  if (matched) {
    return matched;
  }

  // Fallback: match the leaf field name anywhere in the template.
  const leaf = [...tokens].reverse().find(token => token.type === 'name');
  if (leaf && leaf.type === 'name') {
    return findByName(template.inputs ?? [], leaf.value);
  }

  return undefined;
};

/**
 * Resolve a user-facing label for a model-input path from the template.
 * Returns the path itself when no label is found.
 */
export const getTemplateInputLabel = (
  template: ITemplate | null | undefined,
  path: string,
): string => {
  const trimmed = path?.trim();
  if (!trimmed) {
    return path;
  }
  if (!template) {
    return trimmed;
  }

  const node = findTemplateNodeByPath(template, trimmed);
  const label = typeof node?.label === 'string' ? node.label.trim() : '';
  return label || trimmed;
};

/**
 * Resolve a select/radio option label for a value at a model-input path.
 * Falls back to a compact string form of the raw value.
 */
export const getTemplateInputValueLabel = (
  template: ITemplate | null | undefined,
  path: string,
  value: unknown,
  maxLength = 36,
): string | null => {
  if (value === undefined || value === null) {
    return null;
  }

  if (template && path?.trim()) {
    const node = findTemplateNodeByPath(template, path.trim());
    const optionLabel = findOptionLabel(node, value);
    if (optionLabel) {
      return truncateLabel(optionLabel, maxLength);
    }
  }

  return truncateLabel(value, maxLength);
};

const findOptionLabel = (node: IBase | undefined, value: unknown): string | undefined => {
  if (!node) {
    return undefined;
  }

  if (node.type === 'select') {
    const match = (node as ISelect).options?.find(option => String(option.value) === String(value));
    return match?.label?.trim() || undefined;
  }

  if (node.type === 'radio') {
    const match = (node as IRadio).options?.find(option => String(option.value) === String(value));
    return match?.label?.trim() || undefined;
  }

  return undefined;
};

const truncateLabel = (value: unknown, maxLength: number): string | null => {
  let text: string;
  if (typeof value === 'string') {
    text = value.trim();
  } else if (typeof value === 'number' || typeof value === 'boolean') {
    text = String(value);
  } else {
    try {
      text = JSON.stringify(value);
    } catch {
      text = String(value);
    }
  }

  if (!text) {
    return null;
  }

  if (text.length > maxLength) {
    return `${text.slice(0, maxLength - 1)}…`;
  }

  return text;
};
